import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/services/api';

interface User {
    id: number;
    username: string;
    fullName: string;
    email: string;
    role: 'ADMINISTRADOR' | 'COORDINADOR' | 'SUPERVISOR' | 'OPERADOR';
    institution: {
        id: number;
        name: string;
        code: string;
    };
    unit?: {
        id: number;
        name: string;
        code: string;
    };
    status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
    createdAt: string;
    lastLogin?: string;
}

interface Institution {
    id: number;
    name: string;
    code: string;
    units: { id: number; name: string; code: string }[];
}

const roleLabels = {
    ADMINISTRADOR: { labelKey: 'admin.users.roles.ADMINISTRADOR', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
    COORDINADOR: { labelKey: 'admin.users.roles.COORDINADOR', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
    SUPERVISOR: { labelKey: 'admin.users.roles.SUPERVISOR', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    OPERADOR: { labelKey: 'admin.users.roles.OPERADOR', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
};

const statusLabels = {
    ACTIVE: { labelKey: 'admin.users.statuses.ACTIVE', color: 'bg-green-500/20 text-green-400' },
    INACTIVE: { labelKey: 'admin.users.statuses.INACTIVE', color: 'bg-gray-500/20 text-gray-400' },
    SUSPENDED: { labelKey: 'admin.users.statuses.SUSPENDED', color: 'bg-red-500/20 text-red-400' },
};

// Datos de demostración con estructura institución → unidad
const demoUsers: User[] = [
    { id: 1, username: 'admin.central', fullName: 'Juan Pérez González', email: 'admin@vantak.io', role: 'ADMINISTRADOR', institution: { id: 4, name: 'Administración Central VANTAK', code: 'ADMIN' }, status: 'ACTIVE', createdAt: '2024-01-15', lastLogin: '2024-12-12T10:30:00' },
    { id: 2, username: 'cmd.pdi.rm', fullName: 'María Fernández López', email: 'mfernandez@demo.vantak.io', role: 'COORDINADOR', institution: { id: 2, name: 'Policía de Investigaciones', code: 'PDI' }, unit: { id: 201, name: 'Brigada de Homicidios', code: 'PDI-BH' }, status: 'ACTIVE', createdAt: '2024-02-20', lastLogin: '2024-12-12T09:15:00' },
    { id: 3, username: 'sup.carabineros', fullName: 'Carlos Rodríguez Soto', email: 'crodriguez@demo.vantak.io', role: 'SUPERVISOR', institution: { id: 1, name: 'Carabineros de Chile', code: 'CAR' }, unit: { id: 101, name: 'Prefectura Santiago Centro', code: 'CAR-STC' }, status: 'ACTIVE', createdAt: '2024-03-10', lastLogin: '2024-12-11T18:45:00' },
    { id: 4, username: 'op.gope.01', fullName: 'Andrea Muñoz Cabrera', email: 'amunoz@demo.vantak.io', role: 'OPERADOR', institution: { id: 1, name: 'Carabineros de Chile', code: 'CAR' }, unit: { id: 102, name: 'GOPE', code: 'GOPE' }, status: 'ACTIVE', createdAt: '2024-04-05', lastLogin: '2024-12-12T08:00:00' },
    { id: 5, username: 'op.os9.01', fullName: 'Roberto Silva Espinoza', email: 'rsilva@demo.vantak.io', role: 'OPERADOR', institution: { id: 1, name: 'Carabineros de Chile', code: 'CAR' }, unit: { id: 103, name: 'Departamento OS-9', code: 'OS9' }, status: 'SUSPENDED', createdAt: '2024-05-12', lastLogin: '2024-11-28T14:30:00' },
    { id: 6, username: 'op.brianco.01', fullName: 'Patricia Sánchez Rojas', email: 'psanchez@demo.vantak.io', role: 'OPERADOR', institution: { id: 2, name: 'Policía de Investigaciones', code: 'PDI' }, unit: { id: 202, name: 'BRIANCO (Antinarcóticos)', code: 'BRIANCO' }, status: 'ACTIVE', createdAt: '2024-06-01', lastLogin: '2024-12-12T07:30:00' },
];

const demoInstitutions: Institution[] = [
    {
        id: 1, name: 'Carabineros de Chile', code: 'CAR', units: [
            { id: 101, name: 'Prefectura Santiago Centro', code: 'CAR-STC' },
            { id: 102, name: 'GOPE', code: 'GOPE' },
            { id: 103, name: 'Departamento OS-9', code: 'OS9' },
            { id: 104, name: 'Prefectura Aeropolicial', code: 'CAR-AERO' },
        ]
    },
    {
        id: 2, name: 'Policía de Investigaciones', code: 'PDI', units: [
            { id: 201, name: 'Brigada de Homicidios', code: 'PDI-BH' },
            { id: 202, name: 'BRIANCO (Antinarcóticos)', code: 'BRIANCO' },
            { id: 203, name: 'Brigada de Robos', code: 'PDI-BR' },
        ]
    },
    {
        id: 3, name: 'Fiscalía Nacional', code: 'FIS', units: [
            { id: 301, name: 'Fiscalía Metropolitana Centro', code: 'FIS-MC' },
        ]
    },
    { id: 4, name: 'Administración Central VANTAK', code: 'ADMIN', units: [] },
];

export default function UsersPage() {
    const { t } = useTranslation();
    const [users, setUsers] = useState<User[]>([]);
    const [institutions, setInstitutions] = useState<Institution[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState<string>('');
    const [filterInstitution, setFilterInstitution] = useState<string>('');
    const [filterOperation, setFilterOperation] = useState<string>('');
    const [operations, setOperations] = useState<{ id: number; code: string; description: string }[]>([]);
    const [opMemberUserIds, setOpMemberUserIds] = useState<string[]>([]);

    const [formData, setFormData] = useState({
        username: '',
        fullName: '',
        email: '',
        password: '',
        role: 'OPERADOR' as User['role'],
        institutionId: 0,
        unitId: 0,
        status: 'ACTIVE' as User['status'],
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [usersRes, institutionsRes] = await Promise.all([
                api.get('/api/v1/users'),
                api.get('/api/v1/institutions'),
            ]);
            setUsers(usersRes.data);
            setInstitutions(institutionsRes.data);
        } catch {
            setUsers(demoUsers);
            setInstitutions(demoInstitutions);
        } finally {
            setLoading(false);
        }
        // Load operations for filter
        try {
            const opsRes = await api.get('/api/v1/operations?size=200');
            setOperations((opsRes.data?.content || opsRes.data || []).map((o: any) => ({ id: o.id, code: o.code, description: o.description })));
        } catch { /* ignore */ }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (editingUser) {
                await api.put(`/api/v1/users/${editingUser.id}`, formData);
            } else {
                await api.post('/api/v1/users', formData);
            }
            loadData();
            setShowModal(false);
            resetForm();
        } catch {
            // Demo: agregar localmente
            const inst = institutions.find(i => i.id === formData.institutionId);
            const unit = inst?.units.find(u => u.id === formData.unitId);
            const newUser: User = {
                id: users.length + 1,
                username: formData.username,
                fullName: formData.fullName,
                email: formData.email,
                role: formData.role,
                status: formData.status,
                institution: inst || institutions[0],
                unit: unit,
                createdAt: new Date().toISOString(),
            };
            setUsers([...users, newUser]);
            setShowModal(false);
            resetForm();
        }
    };

    const resetForm = () => {
        setFormData({
            username: '',
            fullName: '',
            email: '',
            password: '',
            role: 'OPERADOR',
            institutionId: 0,
            unitId: 0,
            status: 'ACTIVE',
        });
        setEditingUser(null);
    };

    const openEditModal = (user: User) => {
        setEditingUser(user);
        setFormData({
            username: user.username,
            fullName: user.fullName,
            email: user.email,
            password: '',
            role: user.role,
            institutionId: user.institution.id,
            unitId: user.unit?.id || 0,
            status: user.status,
        });
        setShowModal(true);
    };

    // When operation filter changes, load members of that operation
    useEffect(() => {
        if (!filterOperation) { setOpMemberUserIds([]); return; }
        (async () => {
            try {
                const res = await api.get(`/api/v1/operations/${filterOperation}/members`);
                setOpMemberUserIds((res.data || []).map((m: any) => String(m.userId)));
            } catch { setOpMemberUserIds([]); }
        })();
    }, [filterOperation]);

    const filteredUsers = users.filter(user => {
        const matchesSearch = user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesRole = !filterRole || user.role === filterRole;
        const matchesInstitution = !filterInstitution || user.institution?.code === filterInstitution;
        const matchesOperation = !filterOperation || opMemberUserIds.includes(String(user.id));
        return matchesSearch && matchesRole && matchesInstitution && matchesOperation;
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-white">{t('admin.users.title')}</h1>
                    <p className="text-gray-400 mt-1">{t('admin.users.subtitle')}</p>
                </div>
                <button
                    onClick={() => { resetForm(); setShowModal(true); }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                    <span>➕</span>
                    {t('admin.users.newUser')}
                </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-4">
                <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
                    <div className="text-2xl font-bold text-white">{users.length}</div>
                    <div className="text-gray-400 text-sm">{t('admin.users.stats.total')}</div>
                </div>
                <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
                    <div className="text-2xl font-bold text-green-400">{users.filter(u => u.status === 'ACTIVE').length}</div>
                    <div className="text-gray-400 text-sm">{t('admin.users.stats.active')}</div>
                </div>
                <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/30">
                    <div className="text-2xl font-bold text-blue-400">{institutions.length}</div>
                    <div className="text-gray-400 text-sm">{t('admin.users.stats.institutions')}</div>
                </div>
                <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30">
                    <div className="text-2xl font-bold text-red-400">{users.filter(u => u.status === 'SUSPENDED').length}</div>
                    <div className="text-gray-400 text-sm">{t('admin.users.stats.suspended')}</div>
                </div>
            </div>

            {/* Filters */}
            <div className="flex gap-4">
                <div className="flex-1">
                    <input
                        type="text"
                        placeholder={t('admin.users.searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500"
                    />
                </div>
                <select
                    value={filterRole}
                    onChange={(e) => setFilterRole(e.target.value)}
                    className="bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2 text-white"
                >
                    <option value="">{t('admin.users.filters.allRoles')}</option>
                    <option value="ADMINISTRADOR">{t('admin.users.roles.ADMINISTRADOR')}</option>
                    <option value="COORDINADOR">{t('admin.users.roles.COORDINADOR')}</option>
                    <option value="SUPERVISOR">{t('admin.users.roles.SUPERVISOR')}</option>
                    <option value="OPERADOR">{t('admin.users.roles.OPERADOR')}</option>
                </select>
                <select
                    value={filterInstitution}
                    onChange={(e) => setFilterInstitution(e.target.value)}
                    className="bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2 text-white"
                >
                    <option value="">{t('admin.users.filters.allInstitutions')}</option>
                    {institutions.map(inst => (
                        <option key={inst.code} value={inst.code}>{inst.name}</option>
                    ))}
                </select>
                <select
                    value={filterOperation}
                    onChange={(e) => setFilterOperation(e.target.value)}
                    className="bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2 text-white"
                >
                    <option value="">{t('admin.users.filters.allOperations')}</option>
                    {operations.map(op => (
                        <option key={op.id} value={op.id}>{op.code}</option>
                    ))}
                </select>
            </div>

            {/* Users Table */}
            <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 overflow-hidden">
                <table className="w-full">
                    <thead className="bg-gray-700/50">
                        <tr>
                            <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('admin.users.table.user')}</th>
                            <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('admin.users.table.institution')}</th>
                            <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('admin.users.table.role')}</th>
                            <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('admin.users.table.status')}</th>
                            <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('admin.users.table.lastLogin')}</th>
                            <th className="text-right py-3 px-4 text-gray-400 font-medium">{t('admin.common.actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.map(user => (
                            <tr key={user.id} className="border-t border-gray-700/50 hover:bg-gray-700/30">
                                <td className="py-3 px-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                                            <span className="text-white font-medium">{user.fullName.charAt(0)}</span>
                                        </div>
                                        <div>
                                            <div className="text-white font-medium">{user.fullName}</div>
                                            <div className="text-gray-400 text-sm">{user.email}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="py-3 px-4">
                                    <div>
                                        <span className="text-gray-300">{user.institution.name}</span>
                                        {user.unit && (
                                            <div className="text-gray-500 text-xs">{user.unit.name}</div>
                                        )}
                                    </div>
                                </td>
                                <td className="py-3 px-4">
                                    <span className={`px-2 py-1 rounded-lg text-xs border ${roleLabels[user.role].color}`}>
                                        {t(roleLabels[user.role].labelKey)}
                                    </span>
                                </td>
                                <td className="py-3 px-4">
                                    <span className={`px-2 py-1 rounded-lg text-xs ${statusLabels[user.status].color}`}>
                                        {t(statusLabels[user.status].labelKey)}
                                    </span>
                                </td>
                                <td className="py-3 px-4 text-gray-400 text-sm">
                                    {user.lastLogin ? new Date(user.lastLogin).toLocaleString('es-CL') : t('admin.users.never')}
                                </td>
                                <td className="py-3 px-4 text-right">
                                    <button
                                        onClick={() => openEditModal(user)}
                                        className="text-blue-400 hover:text-blue-300 p-2"
                                        title={t('admin.common.edit')}
                                    >
                                        ✏️
                                    </button>
                                    <button
                                        className="text-red-400 hover:text-red-300 p-2"
                                        title={t('admin.users.suspend')}
                                    >
                                        🚫
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-gray-800 rounded-xl p-6 w-full max-w-lg border border-gray-700">
                        <h2 className="text-xl font-bold text-white mb-4">
                            {editingUser ? t('admin.users.editUser') : t('admin.users.newUser')}
                        </h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">{t('admin.users.form.username')}</label>
                                    <input
                                        type="text"
                                        value={formData.username}
                                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">{t('admin.users.form.fullName')}</label>
                                    <input
                                        type="text"
                                        value={formData.fullName}
                                        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                                        required
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">{t('admin.users.form.email')}</label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                                    required
                                />
                            </div>
                            {!editingUser && (
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">{t('admin.users.form.password')}</label>
                                    <input
                                        type="password"
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                                        required={!editingUser}
                                    />
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">{t('admin.users.form.institution')}</label>
                                    <select
                                        value={formData.institutionId}
                                        onChange={(e) => setFormData({ ...formData, institutionId: Number(e.target.value), unitId: 0 })}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                                        required
                                    >
                                        <option value={0}>{t('admin.users.form.selectPlaceholder')}</option>
                                        {institutions.map(inst => (
                                            <option key={inst.id} value={inst.id}>{inst.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">{t('admin.users.form.unit')}</label>
                                    <select
                                        value={formData.unitId}
                                        onChange={(e) => setFormData({ ...formData, unitId: Number(e.target.value) })}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                                    >
                                        <option value={0}>{t('admin.users.form.noSpecificUnit')}</option>
                                        {institutions.find(i => i.id === formData.institutionId)?.units.map(unit => (
                                            <option key={unit.id} value={unit.id}>{unit.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">{t('admin.users.form.role')}</label>
                                <select
                                    value={formData.role}
                                    onChange={(e) => setFormData({ ...formData, role: e.target.value as User['role'] })}
                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                                >
                                    <option value="OPERADOR">{t('admin.users.roles.OPERADOR')}</option>
                                    <option value="SUPERVISOR">{t('admin.users.roles.SUPERVISOR')}</option>
                                    <option value="COORDINADOR">{t('admin.users.roles.COORDINADOR')}</option>
                                    <option value="ADMINISTRADOR">{t('admin.users.roles.ADMINISTRADOR')}</option>
                                </select>
                            </div>
                            {editingUser && (
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">{t('admin.users.form.status')}</label>
                                    <select
                                        value={formData.status}
                                        onChange={(e) => setFormData({ ...formData, status: e.target.value as User['status'] })}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                                    >
                                        <option value="ACTIVE">{t('admin.users.statuses.ACTIVE')}</option>
                                        <option value="INACTIVE">{t('admin.users.statuses.INACTIVE')}</option>
                                        <option value="SUSPENDED">{t('admin.users.statuses.SUSPENDED')}</option>
                                    </select>
                                </div>
                            )}
                            <div className="flex justify-end gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => { setShowModal(false); resetForm(); }}
                                    className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                                >
                                    {t('admin.common.cancel')}
                                </button>
                                <button
                                    type="submit"
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
                                >
                                    {editingUser ? t('admin.users.saveChanges') : t('admin.users.createUser')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
